import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import {
  COMPANY_INFO,
  BENEFITS
} from "../data/siteData";
import ReviewSlider from "../components/ReviewSlider";
import CmsGallery from "../components/CmsGallery";
import ContactForm from "../components/ContactForm";

export default function VinduespudsningPage() {
  const benefits = [
    {
      icon: "fa-location-dot",
      title: "Lokalt på Vestsjælland",
      desc: "Vi kender området og er hurtigt fremme"
    },
    {
      icon: "fa-bolt",
      title: "Hurtig tid",
      desc: "Fleksible aftaler der passer til dig"
    },
    {
      icon: "fa-tag",
      title: "Skarpe priser",
      desc: "Gennemsigtig prisfastsættelse uden skjulte gebyrer"
    },
    {
      icon: "fa-circle-check",
      title: "Professionelt resultat",
      desc: "Kvalitet og grundighed – hver gang"
    }
  ];

  const vinduespudsning2Prices = [
    {
      title: "Udvendig",
      price: "189,-",
      unit: "Pr. Pudsning",
      features: [
        "Et-plans hus",
        "Professionel vinduespolering",
        "Grundig og professionel service",
        "Fast aftale eller enkeltbesøg",
        "Op til 15 Ruder"
      ],
      note: "Perfekt til dig, der ønsker flotte og rene vinduer uden besvær."
    },
    {
      title: "Udvendig & Indvendig",
      price: "345,-",
      unit: "Pr. Pudsning",
      features: [
        "Et-plans hus",
        "Vinduer pudset på begge sider",
        "Grundig og professionel service",
        "Fast aftale eller enkeltbesøg",
        "Op til 15 Ruder"
      ],
      note: "Vores mest populære løsning til boligejere, der ønsker det bedste resultat."
    },
    {
      title: "Indvendig",
      price: "225,-",
      unit: "Pr. Pudsning",
      features: [
        "Et-plans hus",
        "Pudsning af alle indvendige vinduer",
        "Grundig og professionel service",
        "Fast aftale eller enkeltbesøg",
        "Op til 15 Ruder"
      ],
      note: "Ideelt til dig, der ønsker helt rene vinduer indendørs."
    }
  ];

  return (
    <>
      <Helmet>
        <title>Vinduespudsning Vestsjælland | Professionel vinduespolering</title>
        <meta
          name="description"
          content="Få professionel vinduespudsning på Vestsjælland. Vi dækker Slagelse, Sorø, Korsør, Skælskør, Næstved m.fl. Få et uforpligtende tilbud i dag."
        />
        <meta
          property="og:title"
          content="Vinduespudsning Vestsjælland | Professionel vinduespolering"
        />
        <meta
          property="og:description"
          content="Få professionel vinduespudsning på Vestsjælland. Vi dækker Slagelse, Sorø, Korsør, Skælskør, Næstved m.fl. Få et uforpligtende tilbud i dag."
        />
        <meta
          property="og:image"
          content="https://assets.auxodev.dk/assets/e7be4d2d-7d84-4f18-944c-35dc3bc1037c/hero-vinduespudsning-oversigt.png"
        />
        <link rel="canonical" href="https://nf-polering.dk/vinduespudsning" />
      </Helmet>

      {/* Hero */}
      <section className="relative min-h-[620px] flex items-center bg-slate-950 text-white overflow-hidden py-12 sm:py-16 lg:py-20 border-b border-slate-800">
        {/* Background photo & overlay */}
        <div className="absolute inset-0 overflow-hidden">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1920,quality=80,format=auto/assets/e7be4d2d-7d84-4f18-944c-35dc3bc1037c/img-1811_3e36d921.jpg"
            alt="Professionel vinduespudser på Vestsjælland"
            className="w-full h-full object-cover object-center animate-hero-zoom"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950/90 via-slate-950/55 to-slate-950/20" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <div className="lg:col-span-6 space-y-6 text-left">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-cyan-500/10 border border-cyan-400/30 text-cyan-300 text-xs font-semibold">
                <i className="fa-solid fa-sparkles" aria-hidden="true" />
                <span>Professionel vinduespolering på Vestsjælland</span>
              </div>

              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white leading-tight">
                Vinduespudser på Vestsjælland
              </h1>

              <p className="text-base sm:text-lg text-slate-200 leading-relaxed">
                Få hurtig tid, skarpe priser og et resultat der holder. Ring nu eller udfyld formularen – så får du din pris med det samme.
              </p>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-start gap-4 pt-1">
                <a
                  href={COMPANY_INFO.phoneTel}
                  className="inline-flex items-center justify-center gap-3 px-7 py-3.5 rounded-full bg-cyan-500 hover:bg-cyan-400 text-white font-bold text-base transition-all transform active:scale-95 whitespace-nowrap shadow-lg shadow-cyan-500/25"
                >
                  <i className="fa-solid fa-phone" aria-hidden="true" />
                  <span className="whitespace-nowrap">Ring og få en pris</span>
                </a>
              </div>

              {/* Trust Indicators */}
              <div className="pt-6 border-t border-white/20 grid grid-cols-3 gap-4 sm:gap-6 text-left max-w-lg">
                <div>
                  <div className="text-xl sm:text-2xl font-extrabold text-cyan-400">Fra 99,-</div>
                  <div className="text-xs text-slate-300">Gennemsigtige priser</div>
                </div>
                <div>
                  <div className="text-xl sm:text-2xl font-extrabold text-cyan-400">100%</div>
                  <div className="text-xs text-slate-300">Tilfredshedsgaranti</div>
                </div>
                <div>
                  <div className="text-xl sm:text-2xl font-extrabold text-cyan-400">4-12 uger</div>
                  <div className="text-xs text-slate-300">Fast abonnement</div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-6">
              <ContactForm
                id="tilbud"
                title="Udfyld formularen og få et tilbud"
                subtitle="Vi svarer hurtigt med en skarp pris på vinduespudsning."
                defaultService="Vinduespudsning"
                variant="hero"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-12 sm:py-14 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 text-center">
            {benefits.map((benefit, i) => (
              <div
                key={i}
                className="flex flex-col items-center p-2"
              >
                <div className="w-12 h-12 rounded-full bg-cyan-50 border border-cyan-100 flex items-center justify-center text-cyan-600 mb-3 text-lg">
                  <i className={`fa-solid ${benefit.icon}`} aria-hidden="true" />
                </div>
                <h3 className="font-bold text-slate-900 text-base leading-tight">
                  {benefit.title}
                </h3>
                <p className="mt-1.5 text-slate-600 text-sm leading-relaxed w-full mx-auto">
                  {benefit.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Traditionel Vinduespudsning Priser */}
      <section id="priser" className="py-16 sm:py-24 bg-white border-t border-slate-200 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
              Traditionel Vinduespudsning
            </h2>
            <p className="mt-3 text-slate-600 text-base">
              Gennemsigtige priser – så du ved præcis hvad det koster
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch">
            {vinduespudsning2Prices.map((priceCard, idx) => (
              <div
                key={idx}
                className="relative rounded-none p-7 sm:p-8 flex flex-col justify-between transition-all bg-white border border-slate-200 shadow-sm"
              >
                <div>
                  <h3 className="text-xl sm:text-2xl font-extrabold text-slate-900 mb-4">
                    {priceCard.title}
                  </h3>

                  <div className="flex items-baseline gap-1.5 mb-4 pb-4 border-b border-slate-100">
                    <span className="text-3xl sm:text-4xl font-black text-slate-900">
                      {priceCard.price}
                    </span>
                    <span className="text-sm font-semibold text-slate-500">
                      {priceCard.unit}
                    </span>
                  </div>

                  <p className="text-xs sm:text-sm text-slate-600 mb-6 italic">
                    {priceCard.note}
                  </p>

                  <ul className="space-y-3 mb-8 text-sm text-slate-700">
                    {priceCard.features.map((feat, fIdx) => (
                      <li key={fIdx} className="flex items-start gap-2.5">
                        <i className="fa-solid fa-check text-cyan-600 text-sm mt-0.5 shrink-0" aria-hidden="true" />
                        <span>{feat}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <a
                    href={COMPANY_INFO.phoneTel}
                    className="w-full py-3.5 px-4 rounded-full font-bold text-sm text-center transition-all duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] hover:-translate-y-1 transform active:scale-95 flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 text-white"
                  >
                    <span>FÅ TILBUD</span>
                    <i className="fa-solid fa-phone text-xs" aria-hidden="true" />
                  </a>
                </div>
              </div>
            ))}
          </div>

          <p className="text-center text-xs text-slate-500 mt-8 max-w-2xl mx-auto">
            Alle priser er fra-priser og gælder almindelige et-plans huse. Særlige adgangsforhold, meget snavsede ruder eller ekstra arbejde kan medføre tillæg.
          </p>
        </div>
      </section>

      {/* About content sections - Same layout as CityPage */}
      <section className="py-16 sm:py-24 bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-24 sm:space-y-32">
          {/* Row 1: Image Left, Text Right */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <div className="lg:col-span-7 lg:order-2 space-y-5 text-slate-700 leading-relaxed text-base">
              <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900">
                Vinduespudser i Slagelse og på Vestsjælland
              </h2>
              <p>
                Leder du efter en pålidelig vinduespudser i Slagelse, Sorø, Korsør eller resten af Vestsjælland? Hos NF-Polering tilbyder vi professionel vinduespolering til både private boliger, virksomheder og boligforeninger.
              </p>
              <p>
                Vi lægger vægt på kvalitet, punktlighed og god service. Uanset om du ønsker faste pudsninger hver 4., 8. eller 12. uge, eller blot har brug for en enkelt vinduespudsning, står vi klar til at hjælpe.
              </p>
              <p>
                Med faste priser, fleksible aftaler og stor fokus på et flot resultat får du rene vinduer uden besvær. Vi servicerer kunder i hele Vestsjælland, herunder Slagelse, Korsør, Skælskør, Sorø og omegn.
              </p>

              <div className="pt-3">
                <a
                  href="tel:30146070"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-bold text-sm transition-all duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] hover:-translate-y-1 transform active:scale-95"
                >
                  <span>Få et tilbud</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </a>
              </div>
            </div>

            <div className="lg:col-span-5 lg:order-1">
              <div className="rounded-none overflow-hidden border border-slate-200 shadow-sm">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=900,quality=85,format=auto/assets/e7be4d2d-7d84-4f18-944c-35dc3bc1037c/vestsja-lland_091b610e.png"
                  alt="Vestsjælland skilt – Professionel vinduespudsning på Vestsjælland"
                  className="w-full h-80 sm:h-96 object-cover object-[center_20%]"
                />
              </div>
            </div>
          </div>

          {/* Row 2: Text Left, Areas Right */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <div className="lg:col-span-7 lg:order-1 space-y-5 text-slate-700 leading-relaxed text-base">
              <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900">
                Skræddersyede løsninger til dit hjem og erhverv
              </h2>
              <p>
                Uanset om du har et lille hus, en større ejendom eller en erhvervsbygning, finder vi en løsning, der passer til dit behov og dit budget.
              </p>
              <p>
                Vi tilbyder både udvendig og indvendig vinduespolering, afvaskning af karme og rammer samt grundig rengøring af svært tilgængelige vinduer.
              </p>

              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 text-slate-900 font-semibold text-sm">
                <li className="flex items-center gap-2.5">
                  <i className="fa-solid fa-check text-cyan-600 text-sm" aria-hidden="true" />
                  <span>Villaer og parcelhuse</span>
                </li>
                <li className="flex items-center gap-2.5">
                  <i className="fa-solid fa-check text-cyan-600 text-sm" aria-hidden="true" />
                  <span>Rækkehuse og lejligheder</span>
                </li>
                <li className="flex items-center gap-2.5">
                  <i className="fa-solid fa-check text-cyan-600 text-sm" aria-hidden="true" />
                  <span>Sommerhuse</span>
                </li>
                <li className="flex items-center gap-2.5">
                  <i className="fa-solid fa-check text-cyan-600 text-sm" aria-hidden="true" />
                  <span>Erhverv og butikker</span>
                </li>
              </ul>

              <div className="pt-3">
                <a
                  href="tel:30146070"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-bold text-sm transition-all duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] hover:-translate-y-1 transform active:scale-95"
                >
                  <span>Få et tilbud</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </a>
              </div>
            </div>

            <div className="lg:col-span-5 lg:order-2">
              <div className="bg-white rounded-none p-6 sm:p-8 border border-slate-200 shadow-sm space-y-4">
                <h3 className="font-bold text-slate-900 text-base uppercase tracking-wider">
                  Find din lokale vinduespudser:
                </h3>
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 text-sm text-slate-700">
                  {[
                    { label: "Slagelse", href: "/vinduespudser-slagelse" },
                    { label: "Sorø", href: "/vinduespudser-soroe" },
                    { label: "Skælskør", href: "/vinduespudser-skaelskoer" },
                    { label: "Dalmose", href: "/vinduespudser-dalmose" },
                    { label: "Næstved", href: "/vinduespudser-naestved" },
                    { label: "Karrebæksminde", href: "/vindusepudser-karrebaeksminde" },
                    { label: "Ringsted", href: "/vinduespudser-ringsted" },
                    { label: "Høng", href: "/vinduespudser-hoeng" },
                    { label: "Fuglebjerg", href: "/vindusepudser-fuglebjerg" }
                  ].map((sub, idx, arr) => (
                    <React.Fragment key={sub.href}>
                      <Link
                        to={sub.href}
                        className="font-medium text-slate-900 hover:text-cyan-600 transition-colors"
                      >
                        {sub.label}
                      </Link>
                      {idx < arr.length - 1 && (
                        <span className="text-slate-300 font-normal">•</span>
                      )}
                    </React.Fragment>
                  ))}
                </div>
                <div className="pt-2">
                  <Link
                    to="/daekningsomraader"
                    className="inline-flex items-center gap-1 text-xs font-bold text-slate-900 hover:text-cyan-600 transition-colors"
                  >
                    <span>Se alle dækningsområder &amp; postnumre</span>
                    <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Dynamic Gallery */}
      <CmsGallery
        slug="vinduespudsning"
        title="Billeder af vinduespolering"
        subtitle="Se eksempler på vores arbejde med vinduespolering på Vestsjælland."
      />

      {/* Review slider */}
      <ReviewSlider />
    </>
  );
}
